import SmartToyIcon from '@mui/icons-material/SmartToy'
import SendIcon from '@mui/icons-material/Send'
import CloseIcon from '@mui/icons-material/Close'
import {
  Badge,
  Box,
  CircularProgress,
  Divider,
  Drawer,
  Fab,
  IconButton,
  Paper,
  Chip,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useEffect, useMemo, useRef, useState } from 'react'
import { normalizeAgentPlan } from './actionTypes'
import { runAgentActions } from './actionRunner'
import { useAgentBridgeRegistry } from './bridgeRegistry'
import { usePlanAgentActionsMutation } from '../store/api/agentApi'

interface ChatMessage {
  role: 'user' | 'assistant'
  text: string
}

export default function ChatWidget() {
  const theme = useTheme()
  const { activeModule, getBridge } = useAgentBridgeRegistry()
  const [planAgentActions] = usePlanAgentActionsMutation()
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: 'assistant',
      text: 'Hello, I can assist with form Sale/Purchase operations using natural language commands.',
    },
  ])

  const hasActiveModule = Boolean(activeModule)
  const headerText = useMemo(
    () => (activeModule ? `Agent active on: ${activeModule}` : 'Not on a supported module'),
    [activeModule],
  )

  const appendMessage = (role: ChatMessage['role'], text: string) =>
    setMessages((prev) => [...prev, { role, text }])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  const submit = async () => {
    const prompt = input.trim()
    if (!prompt || loading) return

    appendMessage('user', prompt)
    setInput('')
    setLoading(true)

    try {
      const raw = await planAgentActions({
        message: prompt,
        activeModule: activeModule ?? null,
      }).unwrap()
      const plan = normalizeAgentPlan(raw)
      appendMessage('assistant', plan.reply)

      if (plan.actions.length > 0) {
        const results = await runAgentActions(plan.actions, { activeModule, getBridge })
        const resultLines = results.map((r, idx) => `${idx + 1}. [${r.status}] ${r.message}`)
        appendMessage('assistant', `Execution results:\n${resultLines.join('\n')}`)
      }
    } catch (error) {
      appendMessage(
        'assistant',
        error instanceof Error ? error.message : 'Agent encountered an error while processing the request.',
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Badge
        color={hasActiveModule ? 'success' : 'warning'}
        variant="dot"
        overlap="circular"
        sx={{ position: 'fixed', right: 24, bottom: 24, zIndex: 1400 }}
      >
        <Fab color="primary" onClick={() => setOpen(true)}>
          <SmartToyIcon />
        </Fab>
      </Badge>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: 430,
            maxWidth: '92vw',
            borderTopLeftRadius: 14,
            borderBottomLeftRadius: 14,
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
          <Box
            sx={{
              px: 2,
              py: 1.25,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'common.white',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <SmartToyIcon sx={{ fontSize: 20 }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                CRUD Agent
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.95 }}>
                {headerText}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={hasActiveModule ? 'Ready' : 'Waiting'}
              color={hasActiveModule ? 'success' : 'warning'}
              sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'common.white' }}
            />
            <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'common.white' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ flex: 1, overflow: 'auto', px: 1.5, py: 1.25 }}>
            {messages.map((m, index) => (
              <Box
                key={`${m.role}-${index}`}
                sx={{
                  py: 0.6,
                  display: 'flex',
                  justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                <Paper
                  elevation={1}
                  sx={{
                    px: 1.2,
                    py: 0.95,
                    maxWidth: '88%',
                    borderRadius: 2,
                    backgroundColor: m.role === 'user' ? 'primary.main' : 'background.paper',
                    color: m.role === 'user' ? 'primary.contrastText' : 'text.primary',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      color: m.role === 'user' ? 'rgba(255,255,255,0.84)' : 'text.secondary',
                    }}
                  >
                    {m.role}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {m.text}
                  </Typography>
                </Paper>
              </Box>
            ))}
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', py: 0.6 }}>
                <Paper elevation={0} sx={{ px: 1.2, py: 0.8, borderRadius: 2, bgcolor: 'background.paper' }}>
                  <Typography variant="body2" color="text.secondary">
                    Agent is processing...
                  </Typography>
                </Paper>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>
          <Divider />
          <Box sx={{ p: 1.25, bgcolor: 'background.paper' }}>
            <TextField
              size="small"
              fullWidth
              multiline
              maxRows={4}
              placeholder="e.g. open a new sale form and set clientCode = A001"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  void submit()
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton color="primary" onClick={() => void submit()} disabled={loading || !input.trim()}>
                      {loading ? <CircularProgress size={18} /> : <SendIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>
      </Drawer>
    </>
  )
}
